import Alpine from 'alpinejs';

const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const desktopHoverCloseDelay = 180;

export function headerNavigation() {
    return {
        isDesktop: false,
        mobileOpen: false,
        openItemId: null,
        signInOpen: false,
        pendingHoverClose: null,
        modalReturnTrigger: null,
        ownsScrollLock: false,
        inertElements: new Map(),

        init() {
            this.breakpoint = window.matchMedia('(min-width: 1280px)');
            this.isDesktop = this.breakpoint.matches;
            this.onBreakpointChange = () => {
                const hadOpenSurface =
                    this.mobileOpen ||
                    this.openItemId !== null ||
                    this.signInOpen;
                this.closeAll();
                this.isDesktop = this.breakpoint.matches;
                if (hadOpenSurface) {
                    this.$nextTick(() =>
                        this.focusTrigger(
                            this.isDesktop
                                ? this.$root.querySelector(
                                      '[data-navigation-trigger]'
                                  )
                                : this.$refs.menuToggle
                        )
                    );
                }
            };
            this.breakpoint.addEventListener('change', this.onBreakpointChange);
        },

        destroy() {
            this.breakpoint.removeEventListener(
                'change',
                this.onBreakpointChange
            );
            this.closeAll();
        },

        focusTrigger(trigger) {
            trigger?.focus({ preventScroll: true });
        },

        visibleControls(element) {
            return [...element.querySelectorAll(focusableSelector)].filter(
                (control) =>
                    control.getClientRects().length &&
                    !control.closest('[inert]')
            );
        },

        cancelHoverClose() {
            if (this.pendingHoverClose === null) return;
            window.clearTimeout(this.pendingHoverClose);
            this.pendingHoverClose = null;
        },

        scheduleHoverClose(callback) {
            this.cancelHoverClose();
            this.pendingHoverClose = window.setTimeout(() => {
                this.pendingHoverClose = null;
                callback();
            }, desktopHoverCloseDelay);
        },

        openItem(id) {
            if (!this.isDesktop && !this.mobileOpen) return;
            this.cancelHoverClose();
            this.signInOpen = false;
            this.openItemId = id;
        },

        activateItem(id) {
            // Hover already opens desktop items before click; only keyboard and mobile activation toggle.
            if (this.isDesktop) this.openItem(id);
            else this.toggleItem(id);
        },

        toggleItem(id) {
            this.cancelHoverClose();
            if (this.openItemId === id) this.openItemId = null;
            else this.openItem(id);
        },

        leaveItem(id, event) {
            if (!this.isDesktop) return;
            const leftItem = !event.currentTarget.contains(event.relatedTarget);
            if (!leftItem) return;

            // Give diagonal pointer movement time to reach the wider panel below.
            if (event.type === 'mouseleave') {
                if (event.currentTarget.contains(document.activeElement))
                    return;
                this.scheduleHoverClose(() => {
                    if (this.openItemId === id) this.openItemId = null;
                });
                return;
            }

            this.cancelHoverClose();
            if (this.openItemId === id) this.openItemId = null;
        },

        focusPanel(id) {
            this.openItem(id);
            this.$nextTick(() => {
                const panel = document.getElementById(`navigation-panel-${id}`);
                this.visibleControls(panel)[0]?.focus();
            });
        },

        openSignIn() {
            this.cancelHoverClose();
            if (this.mobileOpen) {
                this.closeAll();
                this.focusTrigger(this.$refs.signInTrigger);
            }
            this.openItemId = null;
            this.signInOpen = true;
        },

        activateSignIn() {
            if (this.isDesktop) this.openSignIn();
            else this.toggleSignIn();
        },

        toggleSignIn() {
            this.cancelHoverClose();
            if (this.signInOpen) this.signInOpen = false;
            else this.openSignIn();
        },

        leaveSignIn(event) {
            const leftDisclosure = !event.currentTarget.contains(
                event.relatedTarget
            );
            if (!leftDisclosure) return;

            if (
                event.type === 'mouseleave' &&
                event.currentTarget.contains(document.activeElement)
            )
                return;

            if (event.type === 'mouseleave' && this.isDesktop) {
                this.scheduleHoverClose(() => {
                    this.signInOpen = false;
                });
                return;
            }

            this.cancelHoverClose();
            this.signInOpen = false;
        },

        focusSignInPanel() {
            this.openSignIn();
            this.$nextTick(() =>
                this.visibleControls(this.$refs.signInPanel)[0]?.focus()
            );
        },

        toggleMobile() {
            if (this.mobileOpen) {
                this.closeAll();
                this.focusTrigger(this.$refs.menuToggle);
                return;
            }
            if (this.isDesktop) return;
            this.openItemId = null;
            this.signInOpen = false;
            this.mobileOpen = true;
            this.ownsScrollLock = !this.$store.body.scrollLocked;
            if (this.ownsScrollLock) this.$store.body.scrollLocked = true;
            // Preserve pre-existing inert state and exclude dialogs, which own their native focus trap.
            for (
                let branch = this.$root;
                branch.parentElement;
                branch = branch.parentElement
            ) {
                for (const sibling of branch.parentElement.children) {
                    if (
                        sibling === branch ||
                        sibling.tagName === 'DIALOG' ||
                        ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)
                    )
                        continue;
                    this.inertElements.set(sibling, sibling.inert);
                    sibling.inert = true;
                }
                if (branch.parentElement === document.body) break;
            }
            this.$nextTick(() =>
                // A simultaneous Sign In fade can release Alpine's next ticks before navigation is displayed.
                window.requestAnimationFrame(() => {
                    if (this.mobileOpen)
                        this.visibleControls(this.$refs.navigation)[0]?.focus();
                })
            );
        },

        closeAll() {
            this.cancelHoverClose();
            this.openItemId = null;
            this.signInOpen = false;
            this.mobileOpen = false;
            for (const [element, wasInert] of this.inertElements)
                element.inert = wasInert;
            this.inertElements.clear();
            if (this.ownsScrollLock) this.$store.body.scrollLocked = false;
            this.ownsScrollLock = false;
        },

        dismissOutside(event) {
            if (!this.$root.contains(event.target)) this.closeAll();
        },

        handleEscape(event) {
            this.cancelHoverClose();
            let trigger = null;
            if (this.signInOpen) {
                this.signInOpen = false;
                trigger = this.$refs.signInTrigger;
            } else if (this.openItemId !== null) {
                trigger = this.$root.querySelector(
                    `[data-navigation-trigger="${this.openItemId}"]`
                );
                this.openItemId = null;
            } else if (this.mobileOpen) {
                this.closeAll();
                trigger = this.$refs.menuToggle;
            } else return;
            event.preventDefault();
            event.stopPropagation();
            this.focusTrigger(trigger);
        },

        trapFocus(event) {
            if (!this.mobileOpen || document.querySelector('dialog[open]'))
                return;
            const controls = this.visibleControls(this.$root);
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (
                (event.shiftKey && document.activeElement === first) ||
                (!event.shiftKey && document.activeElement === last)
            ) {
                event.preventDefault();
                this.focusTrigger(event.shiftKey ? last : first);
            }
        },

        followTarget(event) {
            if (!event.target.closest('[data-header-navigation-target]'))
                return;
            const returnTrigger = this.mobileOpen
                ? this.$refs.menuToggle
                : this.openItemId !== null
                  ? this.$root.querySelector(
                        `[data-navigation-trigger="${this.openItemId}"]`
                    )
                  : this.$refs.signInTrigger;
            this.closeAll();
            // Focus a persistent trigger before an action opens a dialog, so native dialog close restores it.
            if (returnTrigger) this.prepareModalReturn(returnTrigger);
        },

        prepareModalReturn(trigger) {
            this.focusTrigger(trigger);
            this.modalReturnTrigger = trigger;
            // Existing actions open dialogs synchronously; do not retain this trigger for an unrelated later modal.
            this.$nextTick(() => {
                this.modalReturnTrigger = null;
            });
        },

        modalOpened(event) {
            const trigger = this.modalReturnTrigger;
            this.modalReturnTrigger = null;
            this.closeAll();
            if (trigger && event.target.tagName === 'DIALOG') {
                event.target.addEventListener(
                    'close',
                    () => {
                        this.openItemId = null;
                        this.signInOpen = false;
                        this.focusTrigger(
                            trigger.getClientRects().length
                                ? trigger
                                : this.isDesktop
                                  ? this.$root.querySelector(
                                        '[data-navigation-trigger]'
                                    )
                                  : this.$refs.menuToggle
                        );
                    },
                    { once: true }
                );
            }
        },

        guestLogin(event) {
            this.closeAll();
            this.prepareModalReturn(this.$refs.signInTrigger);
            const modal = document.getElementById('sign-in-modal');
            if (modal) {
                event.preventDefault();
                this.$store.modals.open(modal);
            } else if (window.location.pathname === '/sign-in') {
                event.preventDefault();
                document.getElementById('loginName-signin')?.focus();
            }
        }
    };
}

Alpine.data('headerNavigation', headerNavigation);
